import { createTheme } from "@mui/material/styles";

const adminFont =
  '"Poppins","Inter","Segoe UI","Roboto","Helvetica","Arial",sans-serif';

// Brand palette borrowed from the customer storefront:
//   cream #fffbf2 / beige #f8ecd7, dusty-rose #b28585 (hover #b98f8f),
//   warm browns #5e3e3e / #511d00, gold #b2a885 — Poppins.
const customTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#b28585",
      light: "#b98f8f",
      dark: "#5e3e3e",
      contrastText: "#fff",
    },
    secondary: {
      // warm gold — mirrors the brand logo
      main: "#b2a885",
      light: "#c8bf9f",
      dark: "#8a7a4f",
      contrastText: "#fff",
    },
    success: { main: "#2f9e69" },
    info: { main: "#5a8fb0" },
    warning: { main: "#d9952f" },
    error: { main: "#c0392b" },
    white: { main: "#fff" },
    orange: { main: "#ffdb0f" },
    background: {
      default: "#fffbf2",
      paper: "#ffffff",
    },
    text: {
      primary: "#4a2e2e",
      secondary: "#9a7f7f",
    },
    divider: "rgba(94,62,62,0.14)",
  },
  shape: { borderRadius: 14 },
  typography: {
    fontFamily: adminFont,
    h5: { fontWeight: 700, letterSpacing: 0.2, color: "#511d00" },
    h6: { fontWeight: 600, letterSpacing: 0.2, color: "#511d00" },
    subtitle1: { fontWeight: 600 },
    button: { textTransform: "none", fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background:
            "radial-gradient(1100px 560px at 100% -5%, #f8ecd7 0%, transparent 55%)," +
            "radial-gradient(900px 480px at -5% 105%, rgba(178,133,133,0.16) 0%, transparent 55%)," +
            "#fffbf2",
          backgroundAttachment: "fixed",
        },
        "*::-webkit-scrollbar": { width: 8, height: 8 },
        "*::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(178,133,133,0.45)",
          borderRadius: 8,
        },
        "*::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "rgba(178,133,133,0.65)",
        },
      },
    },
    MuiPaper: {
      styleOverrides: { root: { backgroundImage: "none" } },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "#ffffff",
          border: "1px solid rgba(94,62,62,0.09)",
          borderRadius: 18,
          boxShadow: "0 6px 18px rgba(94,62,62,0.05)",
          backgroundImage: "none",
          overflow: "hidden",
          transition: "box-shadow .22s ease, transform .22s ease",
          "&:hover": { boxShadow: "0 12px 30px rgba(94,62,62,0.11)" },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          paddingBottom: 8,
          // full-bleed divider under the card title — touches both card edges
          borderBottom: "1px solid rgba(94,62,62,0.12)",
        },
        title: {
          fontSize: "1.15rem",
          fontWeight: 700,
          letterSpacing: 0.2,
          color: "#511d00",
        },
        subheader: { color: "#9a7f7f" },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          // full-width underline on the whole header row (spans edge-to-edge)
          "& .MuiTableRow-root": {
            borderBottom: "1.5px solid rgba(94,62,62,0.18)",
          },
          "& .MuiTableCell-head": {
            color: "#9a7f7f",
            fontWeight: 700,
            fontSize: "0.72rem",
            letterSpacing: "0.09em",
            textTransform: "uppercase",
            backgroundColor: "transparent",
            borderBottom: "none",
            paddingTop: 14,
            paddingBottom: 14,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: "1px solid rgba(94,62,62,0.08)",
          fontSize: "0.875rem",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: "background-color .15s ease",
          "&:hover": { backgroundColor: "rgba(178,133,133,0.08) !important" },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 10, fontWeight: 600, paddingInline: 18 },
        containedPrimary: {
          background: "linear-gradient(135deg,#b28585 0%,#5e3e3e 100%)",
          boxShadow: "0 6px 16px rgba(178,133,133,0.40)",
          "&:hover": {
            background: "linear-gradient(135deg,#b98f8f 0%,#5e3e3e 100%)",
            boxShadow: "0 8px 20px rgba(178,133,133,0.50)",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 8, fontWeight: 600, letterSpacing: 0.3 },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "#fffdf8",
          backgroundImage:
            "linear-gradient(180deg, rgba(178,133,133,0.10) 0%, transparent 26%)",
          borderRight: "1px solid rgba(94,62,62,0.12)",
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          marginInline: 12,
          marginBlock: 4,
          color: "#5e3e3e",
          transition: "all .18s ease",
          "& .MuiListItemIcon-root": { color: "#b28585", minWidth: 40 },
          "&:hover": {
            backgroundColor: "rgba(178,133,133,0.12)",
            color: "#511d00",
          },
          "&.Mui-selected": {
            background:
              "linear-gradient(135deg, rgba(178,133,133,0.26), rgba(178,133,133,0.10))",
            color: "#511d00",
            boxShadow: "inset 3px 0 0 0 #b28585",
            "& .MuiListItemIcon-root": { color: "#5e3e3e" },
            "&:hover": {
              background:
                "linear-gradient(135deg, rgba(178,133,133,0.32), rgba(178,133,133,0.14))",
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(255,251,242,0.85)",
          backgroundImage: "none",
          backdropFilter: "blur(16px)",
          borderBottom: "1px solid rgba(94,62,62,0.12)",
          boxShadow: "none",
          color: "#5e3e3e",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: "#ffffff",
          border: "1px solid rgba(94,62,62,0.12)",
          borderRadius: 12,
          marginTop: 6,
          boxShadow: "0 12px 34px rgba(94,62,62,0.16)",
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 600,
          color: "#5e3e3e",
          borderColor: "rgba(94,62,62,0.20)",
          "&.Mui-selected": {
            background: "linear-gradient(135deg,#b28585 0%,#5e3e3e 100%)",
            color: "#fff",
            borderColor: "transparent",
          },
        },
      },
    },
  },
});

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#9155FD",
    },
    secondary: {
      main: "#f48fb1",
    },
  },
});

const customerTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#b28585",
      light: "#b98f8f",
      dark: "#5e3e3e",
      contrastText: "#fff",
    },
    secondary: {
      main: "#b2a885",
      light: "#c8bf9f",
      dark: "#8a7a4f",
      contrastText: "#fff",
    },
    white: { main: "#fff" },
    orange: { main: "#ffdb0f" },
    text: {
      primary: "#3d2b2b",
      secondary: "#8a7575",
    },
    background: {
      default: "#fffbf2",
      paper: "#ffffff",
    },
  },
  shape: { borderRadius: 12 },
  typography: {
    fontFamily: '"Poppins","Inter","Helvetica","Arial",sans-serif',
    button: { textTransform: "none", fontWeight: 600 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 10, fontWeight: 600, paddingInline: 18 },
        containedPrimary: {
          background: "linear-gradient(135deg,#b28585 0%,#5e3e3e 100%)",
          boxShadow: "0 6px 16px rgba(178,133,133,0.35)",
          "&:hover": {
            background: "linear-gradient(135deg,#b98f8f 0%,#5e3e3e 100%)",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: 8, fontWeight: 600 } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#b28585",
          },
        },
      },
    },
  },
});

export { customTheme, darkTheme, customerTheme };
